/**
 * Resolve team-target `timesheet_approve` rules against a Monday users list.
 *
 * Given a set of permission rule targets (each `targetType: 'user' | 'team' | 'all'`)
 * and the full Monday users list (each user carries their team memberships),
 * produce:
 *   - `timesheetApproveeIds`: user ids the current user can act on behalf of,
 *     expanded from both direct 'user' targets and resolved 'team' targets.
 *   - `canLogTimeForAnyUser`: true when any 'all' target exists (bypasses the
 *     explicit id set).
 *
 * Unknown `targetType` values are ignored defensively.
 */
export interface ApproveTarget {
  targetType: string;
  targetId: string | null;
}

export interface UserWithTeams {
  id: number;
  teams?: Array<{ id: number; name?: string }>;
}

export interface ApproveeResolution {
  timesheetApproveeIds: Set<number>;
  canLogTimeForAnyUser: boolean;
}

export function resolveApprovees(
  targets: ApproveTarget[],
  users: UserWithTeams[],
): ApproveeResolution {
  const ids = new Set<number>();
  let canAll = false;
  const teamTargetIds = new Set<string>();

  for (const t of targets) {
    if (t.targetType === 'all') {
      canAll = true;
      continue;
    }
    if (t.targetType === 'user' && t.targetId) {
      const n = Number(t.targetId);
      if (n) ids.add(n);
    } else if (t.targetType === 'team' && t.targetId) {
      teamTargetIds.add(t.targetId);
    }
    // Unknown targetType values are ignored (defensive).
  }

  if (teamTargetIds.size > 0 && users.length > 0) {
    for (const u of users) {
      if (!u.teams || u.teams.length === 0) continue;
      for (const team of u.teams) {
        if (teamTargetIds.has(String(team.id))) {
          ids.add(u.id);
          break;
        }
      }
    }
  }

  return { timesheetApproveeIds: ids, canLogTimeForAnyUser: canAll };
}
